import { useEffect, useState } from "react";
import { Button } from "../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../design/dialog";
import { Input } from "../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../design/fieldset";
import { PlusIcon } from "@heroicons/react/20/solid";
import { MatterService } from "../services/MatterService";
import { useMatter } from "../contexts/MatterContext";
import { HolidaySchedule } from "../models/HolidaySchedule";
import { HolidayScheduleService } from "../services/HolidayScheduleService";
import { Select } from "../design/select";
import { HolidayScheduleStatus } from "../models/HolidayScheduleStatus";

interface Props {
  schedule?: HolidaySchedule;
  children?: React.ReactNode;
  className?: string;
  onSave?: (id: HolidaySchedule["_id"]) => void;
}

interface Errors {
  saving?: string;
  name?: string;
  status?: string;
}

const HolidayScheduleButton: React.FC<Props> = ({
  schedule,
  children,
  className,
  onSave,
}) => {
  const { currentMatterId } = useMatter();
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [name, setName] = useState("");
  const [status, setStatus] = useState<HolidayScheduleStatus>("draft");
  const [errors, setErrors] = useState<Errors>({});

  useEffect(() => {
    if (schedule && isOpen) {
      setName(schedule.name || "");
      setStatus(schedule.status || "draft");
    }
  }, [schedule, isOpen]);

  const validateForm = () => {
    const newErrors: { name?: string } = {};
    if (!name.trim()) newErrors.name = "Name is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setIsSaving(true);
    setErrors({});
    try {
      if (schedule) {
        await HolidayScheduleService.update(schedule._id, {
          name,
        });
        if (onSave) onSave(schedule._id);
      } else {
        const id = await MatterService.createHolidaySchedule(currentMatterId!, {
          name,
          status,
          matterId: currentMatterId!,
          holidayRules: [],
        });
        if (onSave) onSave(id);
      }
      setIsOpen(false);
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, saving: err.message }));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        className={className}
        onClick={() => setIsOpen(true)}
      >
        {children ?? (
          <div className="flex items-center gap-2">
            {!schedule && <PlusIcon className="w-4 h-4" />}
            <span>{schedule ? "Edit Schedule" : "Create New Schedule"}</span>
          </div>
        )}
      </Button>

      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>
          {schedule ? "Edit Holiday Schedule" : "Create Holiday Schedule"}
        </DialogTitle>
        <DialogBody>
          {errors.saving && <ErrorMessage>{errors.saving}</ErrorMessage>}
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Schedule Name"
                />
                {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
              </Field>
              {schedule && (
                <Field>
                  <Label>Status</Label>
                  <Select
                    value={status}
                    onChange={(e) =>
                      setStatus(e.target.value as HolidayScheduleStatus)
                    }
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Finalized</option>
                    <option value="archived">Archived</option>
                  </Select>
                  {errors.status && (
                    <ErrorMessage>{errors.status}</ErrorMessage>
                  )}
                </Field>
              )}
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HolidayScheduleButton;
